
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AdminCountryAssignment {
  id: string;
  admin_id: string;
  country_code: string;
  created_at: string;
  created_by?: string;
}

export const useAdminCountryAssignments = () => {
  const queryClient = useQueryClient();

  // Fetch all admin country assignments
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['admin-country-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_country_assignments')
        .select(`
          *,
          profiles!admin_country_assignments_admin_id_fkey(name, email),
          countries(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Get assignments for a specific admin using the new database function
  const getAdminAssignments = async (adminId: string) => {
    const { data, error } = await supabase
      .rpc('get_admin_assigned_countries', { admin_id_param: adminId });
    
    if (error) throw error;
    
    // Transform the data to match the expected format
    return (data || []).map((item: any) => ({
      id: '', // This will be filled by the calling component if needed
      country_code: item.country_code,
      countries: { name: item.country_name }
    }));
  };

  // Get current user's country codes using the new database function
  const getCurrentUserCountries = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    
    const { data, error } = await supabase
      .rpc('get_admin_country_codes', { admin_id_param: user.id });
    
    if (error) throw error;
    return data || [];
  };

  // Assign country to admin
  const assignCountryMutation = useMutation({
    mutationFn: async ({ adminId, countryCode }: { adminId: string; countryCode: string }) => {
      const { data, error } = await supabase
        .from('admin_country_assignments')
        .insert({
          admin_id: adminId,
          country_code: countryCode,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-country-assignments'] });
      toast.success('Country assigned successfully');
    },
    onError: (error) => {
      console.error('Error assigning country:', error);
      toast.error('Failed to assign country');
    }
  });

  // Remove country assignment
  const removeAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('admin_country_assignments')
        .delete()
        .eq('id', assignmentId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-country-assignments'] });
      toast.success('Country assignment removed');
    },
    onError: (error) => {
      console.error('Error removing assignment:', error);
      toast.error('Failed to remove assignment');
    }
  });

  return {
    assignments,
    isLoading,
    getAdminAssignments,
    getCurrentUserCountries,
    assignCountry: assignCountryMutation.mutateAsync,
    removeAssignment: removeAssignmentMutation.mutateAsync,
    isAssigning: assignCountryMutation.isPending,
    isRemoving: removeAssignmentMutation.isPending
  };
};
